import { useEffect, useState } from "react";
import {
  ArrowRight,
  ArrowUp,
  Brain,
  CircleStop,
  Crosshair,
  Flag,
  Gauge,
  Instagram,
  Linkedin,
  Map,
  Target,
  Timer,
} from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { RacingLeadDialog } from "@/components/RacingLeadDialog";
import apexrideLogoWhite from "@/assets/apexride-logo-white.svg";
import heroAsset from "@/assets/racing/rally-swift-hero.jpg";
import suzukiFrontAsset from "@/assets/racing/suzuki-39-garage-front.jpg.asset.json";

import tiresHarnessAsset from "@/assets/racing/rally-tires-harness.jpg.asset.json";
import teamBmwAsset from "@/assets/racing/team-girls-bmw.jpg.asset.json";
import driverRallyAsset from "@/assets/racing/driver-rally.jpg.asset.json";
import driverSturmaneAsset from "@/assets/racing/driver-sturmane.jpg.asset.json";
import porscheGarageAsset from "@/assets/racing/porsche-garage.jpg.asset.json";
import rallyFansKidsAsset from "@/assets/racing/rally-fans-kids.jpg.asset.json";
import suzuki39DoorAsset from "@/assets/racing/suzuki-39-door.jpg.asset.json";
import sprinterAsset from "@/assets/racing/sprinter-racing-team.jpg.asset.json";

// Next event target. Update as the LASF season progresses.
const NEXT_EVENT_AT = new Date("2026-07-03T13:00:00Z");
const NEXT_EVENT_NAME = "LASF Ralio sprinto III etapas";
const NEXT_EVENT_LOCATION = "Kaunas";

function useCountdown(target: Date) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff / 3600000) % 24);
  const m = Math.floor((diff / 60000) % 60);
  const s = Math.floor((diff / 1000) % 60);
  return { d, h, m, s };
}

const pillars = [
  {
    icon: Target,
    tag: "Strategija",
    title: "Rally strategija",
    copy: "Planas, rizikos valdymas ir sprendimai trasoje – ne adrenalinu, o protu.",
  },
  {
    icon: Flag,
    tag: "Pagrindai",
    title: "Vairavimo bazė",
    copy: "Taisyklinga pozicija, akių darbas, rankų darbas ir kūno balansas automobilyje.",
  },
  {
    icon: Map,
    tag: "Pace notes",
    title: "Pace notes",
    copy: "Sisteminga užrašų kalba, kuri leidžia važiuoti greitai ir saugiai dar nematytame kelyje.",
  },
  {
    icon: Crosshair,
    tag: "Apex",
    title: "Apex & linija",
    copy: "Įėjimo, apex ir išėjimo taškai. Kaip išnešti greitį per posūkį, o ne tik įvažiuoti į jį.",
  },
  {
    icon: ArrowUp,
    tag: "Šuoliai",
    title: "Šuoliai & nelygumai",
    copy: "Greičio pasirinkimas, automobilio balansas ore ir saugus nusileidimas.",
  },
  {
    icon: CircleStop,
    tag: "Stabdžiai",
    title: "Stabdymo technika",
    copy: "Stabdymo taškas, slenkamasis stabdymas ir perejimas prie akseleracijos.",
  },
  {
    icon: Brain,
    tag: "Psichologija",
    title: "Psichologija",
    copy: "Vienas svarbiausių dalykų: fokusas, emocijų valdymas, spaudimas ir komandinis darbas.",
  },
  {
    icon: Timer,
    tag: "Slalomas",
    title: "Slalomas",
    copy: "Precizinis vairavimas, reakcija ir kontras tarp konių – puikus įėjimo taškas jauniems vairuotojams.",
  },
  {
    icon: Gauge,
    tag: "Kartingas",
    title: "Kartingas",
    copy: "Įgūdžių pagrindas: trajektorija, stabdymas, akseleracija ir lenktynių kultūra jau nuo mažų vaikų.",
  },
];

const team = [
  { no: "39", role: "Vairuotoja · Trenerė", image: driverRallyAsset.url },
  { no: "39", role: "Šturmanas · Navigacija", image: driverSturmaneAsset.url },
  { no: "—", role: "Inžinieriai & crew", image: teamBmwAsset.url },
  { no: "—", role: "Komanda · Servisas", image: porscheGarageAsset.url },
  { no: "—", role: "Juniorai · Akademija", image: rallyFansKidsAsset.url },
  { no: "39", role: "Livery · Detail", image: suzuki39DoorAsset.url },
];

const season = [
  { round: "01", date: "26 BIR", track: "Sartai / Dusetos", series: "LASF Mini ralis \"Rally Sartai\"", status: "Vykstantis" },
  { round: "02", date: "27 BIR", track: "Kaunas", series: "LASF Street Race III etapas", status: "Planuojama" },
  { round: "03", date: "27 BIR", track: "Aukštaitija", series: "LASF Kroso čempionato II etapas", status: "Planuojama" },
  { round: "04", date: "03 LIE", track: "Kaunas", series: "LASF Ralio sprinto + Extreme rally III etapas", status: "Planuojama" },
  { round: "05", date: "04 LIE", track: "Lietuva", series: "LASF Aplink Lietuvą / FIA Eco Rally Cup", status: "Planuojama" },
  { round: "06", date: "05 LIE", track: "Šakiai", series: "LASF Slalomo čempionato etapas", status: "Planuojama" },
  { round: "07", date: "TBC", track: "Kaunas", series: "Kauno turbožiedinė sankryža · 3 juniorių startas", status: "Planuojama" },
];

const RacingPage = () => {
  useSEO({
    title: "apeXride Racing | Junior Rally Academy",
    description:
      "apeXride Racing Junior Rally Academy, rally mokykla jaunimui 12,25 m. ir moterims. Strategija, pace notes, apex, stabdžiai, psichologija.",
    path: "/",
    image: "https://apexride.racing/og-racing.jpg",
  });

  const { d, h, m, s } = useCountdown(NEXT_EVENT_AT);
  const [leadOpen, setLeadOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-primary selection:text-primary-foreground">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0a0a0a]/80 border-b border-white/5">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src={apexrideLogoWhite} alt="apeXride Racing, grįžti į pradžią" width={110} height={28} className="h-7 w-auto" />
            <span className="hidden sm:inline-block text-[11px] tracking-[0.3em] uppercase text-white/60 border-l border-white/10 pl-3">
              Racing
            </span>
          </a>
          <nav className="hidden md:flex items-center gap-8 text-sm text-white/70">
            <a href="#about" className="hover:text-white transition-colors">Apie</a>
            <a href="#program" className="hover:text-white transition-colors">Programa</a>
            <a href="#team" className="hover:text-white transition-colors">Treneriai</a>
            <a href="#season" className="hover:text-white transition-colors">Sezonas</a>
            <button type="button" onClick={() => setLeadOpen(true)} className="text-white hover:text-primary transition-colors">Susisiekti</button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src={heroAsset}
            alt="apeXride Racing Suzuki Swift trasoje"
            width={1920}
            height={1080}
            className="h-full w-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/80 via-transparent to-[#0a0a0a]/30" />
        </div>

        <div className="container mx-auto px-4 pt-24 pb-32 md:pt-32 md:pb-44">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-primary mb-6">
              <span className="h-px w-8 bg-primary" />
              Junior Rally Academy
            </div>
            <h1 className="font-sans font-black uppercase italic leading-[0.9] tracking-tight text-5xl sm:text-7xl md:text-8xl">
              apeXride
              <br />
              <span className="text-primary">Racing</span>
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-white/70 leading-relaxed">
              Rally mokykla jaunimui nuo 12 iki 25 m. ir visoms moterims, norinčioms įveikti
              savo pirmąją trasą. Mes nepučiame adrenalinu – mes statome įgūdžius, kuriuos
              galima pamatuoti.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setLeadOpen(true)}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-primary/90 transition-colors"
              >
                Pradėk mokytis <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="#program"
                className="inline-flex items-center gap-2 border border-white/20 px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:border-white hover:bg-white/5 transition-colors"
              >
                Programa
              </a>
            </div>
          </div>
        </div>

        {/* Countdown */}
        <div className="relative container mx-auto px-4 -mb-12">
          <div className="bg-white/[0.03] border border-white/10 backdrop-blur-md rounded-sm p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-primary mb-1">Kitas startas</div>
              <div className="text-lg md:text-xl font-semibold">{NEXT_EVENT_NAME}</div>
              <div className="text-sm text-white/60">{NEXT_EVENT_LOCATION}</div>
            </div>
            <div className="grid grid-cols-4 gap-3 md:gap-5 font-mono">
              {[
                { v: d, l: "D" },
                { v: h, l: "H" },
                { v: m, l: "M" },
                { v: s, l: "S" },
              ].map((u) => (
                <div key={u.l} className="text-center min-w-[56px]">
                  <div className="text-3xl md:text-4xl font-bold tabular-nums">
                    {String(u.v).padStart(2, "0")}
                  </div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">{u.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="container mx-auto px-4 pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-4">
            <div className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-3">01 · Apie akademiją</div>
            <h2 className="font-sans font-black uppercase italic text-4xl md:text-5xl leading-none">
              Įgūdžiai, paremti <span className="text-primary">tikslu</span>.
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-5 text-white/70 text-base md:text-lg leading-relaxed">
            <p>
              apeXride Racing yra rally akademija, skirta jauniems vairuotojams ir šturmanams
              nuo 12 iki 25 metų, taip pat visoms moterims, norinčioms įžengti į ralį. Mes
              specializuojamės ne greičio rekorduose kaip pirmame žingsnyje, o technikoje,
              kurią galima kartoti ir tobulinti.
            </p>
            <p>
              Mūsų trenerių komanda yra aukščiausio lygio Lietuvos, Baltijos šalių ir Suomijos
              specialistai. Dirbame skaidriai: kiekvienas mokinys mato savo pažangą, gauna
              konkrečius tikslus ir mokosi ne dėl publikos, o dėl savo rezultato.
            </p>
            <p>
              Programa apima strategiją, pagrindus, pace notes, apex, šuolius, stabdžius ir
              psichologiją – pastaroji yra viena svarbiausių, nes greitis prasideda galvoje.
            </p>
          </div>
        </div>

        {/* Brand fleet strip */}
        <div className="mt-16 grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          <div className="relative bg-[#0a0a0a] aspect-[16/10] overflow-hidden group">
            <img
              src={suzukiFrontAsset.url}
              alt="apeXride Suzuki Swift #39 garaže"
              width={1280}
              height={800}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/90 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="text-[10px] tracking-[0.3em] uppercase text-primary mb-1">Brand Car</div>
              <div className="text-lg font-bold uppercase">Suzuki Swift · apeXride livery</div>
            </div>
          </div>
          <div className="relative bg-[#0a0a0a] aspect-[16/10] overflow-hidden group">
            <img
              src={tiresHarnessAsset.url}
              alt="Rally padangos ir saugos diržai"
              width={1280}
              height={800}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/90 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="text-[10px] tracking-[0.3em] uppercase text-primary mb-1">Setup</div>
              <div className="text-lg font-bold uppercase">Rally padangos · saugos diržai</div>
            </div>
          </div>
        </div>
      </section>

      {/* Program */}
      <section id="program" className="border-t border-white/5 bg-[#070707]">
        <div className="container mx-auto px-4 py-24 md:py-32">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <div className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-3">02 · Programa</div>
              <h2 className="font-sans font-black uppercase italic text-4xl md:text-5xl leading-none">
                Ką mokome
              </h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {pillars.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="group bg-[#070707] p-8 hover:bg-[#0a0a0a] transition-colors relative"
                >
                  <div className="flex items-center justify-between mb-10">
                    <Icon className="w-7 h-7 text-primary" />
                    <span className="text-[10px] tracking-[0.3em] uppercase text-white/60 border border-white/15 px-2 py-1">
                      {p.tag}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold uppercase tracking-tight mb-3">{p.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{p.copy}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <section id="team" className="container mx-auto px-4 py-24 md:py-32">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-3">03 · Treneriai</div>
            <h2 className="font-sans font-black uppercase italic text-4xl md:text-5xl leading-none">
              Komanda
            </h2>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
          {team.map((member) => (
            <article
              key={member.role}
              className="group relative overflow-hidden bg-[#0f0f0f] border border-white/5 aspect-[4/5]"
            >
              <img
                src={member.image}
                alt={member.role}
                width={1024}
                height={1280}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/30 to-transparent" />
              <div className="absolute top-4 right-4 font-sans font-black italic text-[120px] leading-none text-white/10 group-hover:text-primary/40 transition-colors">
                {member.no}
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="text-[10px] tracking-[0.3em] uppercase text-primary">{member.role}</div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Season */}
      <section id="season" className="border-t border-white/5 bg-[#070707]">
        <div className="container mx-auto px-4 py-24 md:py-32">
          <div className="grid md:grid-cols-12 gap-10 mb-12">
            <div className="md:col-span-5">
              <div className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-3">04 · Sezonas 2026</div>
              <h2 className="font-sans font-black uppercase italic text-4xl md:text-5xl leading-none">
                Kalendorius
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <img
                src={sprinterAsset.url}
                alt="apeXride Racing komandos automobilis trasoje"
                width={1280}
                height={960}
                loading="lazy"
                className="w-full aspect-[16/10] object-cover"
              />
            </div>
          </div>

          <div className="border border-white/10">
            <div className="hidden md:grid grid-cols-12 px-6 py-3 text-[10px] tracking-[0.3em] uppercase text-white/60 border-b border-white/10">
              <div className="col-span-1">Rd</div>
              <div className="col-span-2">Data</div>
              <div className="col-span-4">Trasa</div>
              <div className="col-span-3">Serija</div>
              <div className="col-span-2 text-right">Statusas</div>
            </div>
            {season.map((race, i) => (
              <div
                key={race.round}
                className={`grid grid-cols-2 md:grid-cols-12 gap-2 px-6 py-5 items-center border-b border-white/5 last:border-0 ${
                  i % 2 === 0 ? "bg-white/[0.02]" : "bg-transparent"
                }`}
              >
                <div className="md:col-span-1 font-mono text-primary font-bold">{race.round}</div>
                <div className="md:col-span-2 font-mono text-sm text-white/80">{race.date}</div>
                <div className="md:col-span-4 col-span-2 text-base font-semibold uppercase tracking-tight">
                  {race.track}
                </div>
                <div className="md:col-span-3 col-span-2 text-sm text-white/60">{race.series}</div>
                <div className="md:col-span-2 col-span-2 md:text-right">
                  <span
                    className={`inline-block text-[10px] tracking-[0.3em] uppercase px-2 py-1 border ${
                      race.status === "Kitas"
                        ? "border-primary text-primary"
                        : race.status === "Vykstantis"
                        ? "border-primary text-primary bg-primary/10 animate-pulse"
                        : "border-white/15 text-white/50"
                    }`}
                  >
                    {race.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* New track: Kauno turbožiedinė sankryža */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="grid md:grid-cols-12 gap-10 items-center border border-white/10 bg-[#070707] p-8 md:p-12">
          <div className="md:col-span-7">
            <div className="text-[11px] tracking-[0.3em] uppercase text-primary mb-3">Nauja trasa</div>
            <h2 className="font-sans font-black uppercase italic text-3xl md:text-4xl leading-none mb-5">
              Kauno turbožiedinė sankryža
            </h2>
            <p className="text-white/70 leading-relaxed mb-5">
              Naujoji Kauno turbožiedinė sankryža Baltų prospekte tampa iššūkiu, kurio
              pagrindinis tikslas yra ne greitis, o tiksli linija, trajektorija ir sprendimai
              realioje sankryžoje. apeXride Racing įsipareigoja šiame renginyje startuoti su
              trimis jaunaisiais akademijos sportininkais.
            </p>
            <a
              href="https://www.delfi.lt/auto/patarimai/specialistai-paaiskino-kaip-vaziuoti-naujaja-kauno-turboziedine-sankryza-73542406"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-primary hover:text-primary/80 transition-colors"
            >
              Daugiau apie trasą <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className="md:col-span-5">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-[#0a0a0a] border border-white/10 p-5">
                <div className="text-3xl font-bold text-primary">3</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-white/50 mt-2">Juniorai</div>
              </div>
              <div className="bg-[#0a0a0a] border border-white/10 p-5">
                <div className="text-3xl font-bold text-primary">1</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-white/50 mt-2">Trasa</div>
              </div>
              <div className="bg-[#0a0a0a] border border-white/10 p-5">
                <div className="text-3xl font-bold text-primary">0</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-white/50 mt-2">Adrenalino</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="container mx-auto px-4 py-24 md:py-32">
        <div className="relative overflow-hidden border border-white/10 bg-gradient-to-br from-[#0f0f0f] to-[#0a0a0a] p-10 md:p-16">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
          <div className="relative max-w-2xl">
            <div className="text-[11px] tracking-[0.3em] uppercase text-primary mb-4">Pradėk</div>
            <h2 className="font-sans font-black uppercase italic text-3xl md:text-5xl leading-none mb-6">
              Tavo pirmas startas prasideda čia.
            </h2>
            <p className="text-white/70 mb-8 leading-relaxed">
              Nori mokytis ralio jaunųjų ar moterų grupėje? Susisiek ir papasakosime apie
              akademijos programą, artėjančius startus ir kaip greičiausiai pasiekti
              savo pirmąjį finišą.
            </p>
            <button
              type="button"
              onClick={() => setLeadOpen(true)}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-primary/90 transition-colors"
            >
              Susisiekti <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12">
        <div className="container mx-auto px-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 text-xs text-white/60">
          <div className="flex items-center gap-3">
            <img src={apexrideLogoWhite} alt="apeXride Racing, grįžti į pradžią" width={90} height={22} className="h-5 w-auto opacity-70" />
            <span>Racing · dalis apeXride šeimos</span>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 mb-3">Sekite mus</div>
            <ul className="space-y-2">
              <li>
                <a
                  href="https://www.instagram.com/apexride.cars/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 hover:text-white transition-colors"
                >
                  <Instagram className="w-4 h-4 text-primary" />
                  <span>@apexride.cars</span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.instagram.com/apexride.racing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 hover:text-white transition-colors"
                >
                  <Instagram className="w-4 h-4 text-primary" />
                  <span>@apexride.racing</span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.instagram.com/aleks.apexride"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 hover:text-white transition-colors"
                >
                  <Instagram className="w-4 h-4 text-primary" />
                  <span>@aleks.apexride</span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.linkedin.com/feed/dutkevic"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 hover:text-white transition-colors"
                >
                  <Linkedin className="w-4 h-4 text-primary" />
                  <span>LinkedIn</span>
                </a>
              </li>
            </ul>
          </div>
          <div>© {new Date().getFullYear()} apeXride. All rights reserved.</div>
        </div>
      </footer>
      <RacingLeadDialog open={leadOpen} onOpenChange={setLeadOpen} />
    </div>
  );
};

export default RacingPage;
